var AbstractTracker = require("app/Analytics/AbstractTracker"),
    config          = require("app/config"),
    deepEqual       = require("fast-deep-equal"),
    get             = require("object-path").get,
    kind            = require("enyo/kind"),
    utils           = require("enyo/utils");

module.exports = kind(/** @lends  app.Analytics.FacebookPixel.prototype */ {

    /**
     * @private
     */
    name: "app.Analytics.FacebookPixel",

    /**
     * @private
     */
    kind: AbstractTracker,

    /**
     * Whether the tracker is form an IAB Vendor.
     */
    isNonIABVendor: true,

    /**
     * Cached copy of consent data, used to detect consent changes.
     *
     * @private
     */
    consent: null,

    /**
     * @private
     */
    standardEvents: [
        "AddPaymentInfo",
        "AddToCart",
        "AddToWishlist",
        "CompleteRegistration",
        "Contact",
        "CustomizeProduct",
        "Donate",
        "FindLocation",
        "InitiateCheckout",
        "Lead",
        "Purchase",
        "Schedule",
        "Search",
        "StartTrial",
        "SubmitApplication",
        "Subscribe",
        "ViewContent"
    ],

    // Vendor Transparency and Consent
    // ----------------

    /**
     * Respond to changes in consent.
     *
     * @private
     */
    onNonIABConsentDataChange: function() {
        var $privacyService = this.app.$.privacyService,
            consent         = $privacyService.get("nonIABConsentData"),
            consentChanged  = !deepEqual(this.consent, consent);

        // Register new consent data
        if (consentChanged) {
            this.consent = consent;
        }
        else {
            return;
        }

        // We are not responsible for loading ourselves
        if (get(config, "facebookPixel.loader", null) !== "self") {
            return;
        }

        // We aren't ready yet, but are allowed to load.
        if (!this.isReady && this.hasLoadConsent()) {
            this.load();
        }

        // We are ready, but consent has just been revoked.
        else if (this.isReady && !this.hasLoadConsent()) {
            this.app.$.router.reload();
        }
    },

    // Loading
    // ----------------

    /**
     * @returns  {boolean}  Whether we have consent to load the Facebook Pixel
     * @private
     */
    hasLoadConsent: function() {
        var $privacyService   = this.app.$.privacyService,
            nonIABConsentData = $privacyService.get("nonIABConsentData");

        // Bypass parameter set
        if ($privacyService.shouldBypassConsent()) {
            return true;
        }

        // Consent for 'advertisement' purpose required
        else {
            return nonIABConsentData
                && nonIABConsentData.accepted
                && Array.isArray(nonIABConsentData.accepted)
                && nonIABConsentData.accepted.includes("analytics")
                && nonIABConsentData.accepted.includes("advertisement");
        }
    },

    /**
     * @return {string|null} The loader
     * @public
     */
    getLoader: function() {
        return get(config, "facebookPixel.loader", null);
    },

    /**
     * @private
     */
    load: function() {
        var script;

        // Facebook Pixel not enabled
        if (!get(config, "facebookPixel.enabled", false)
            || get(config, "facebookPixel.loader", null) !== "self"
            || !get(config, "facebookPixel.id", false)) {
            return;
        }

        // Bail if already loaded
        if (this.isReady) {
            return;
        }

        // Prepare tracker function
        window.fbq           = window.fbq || function() {
            if (fbq.callMethod) {
                fbq.callMethod.apply(fbq, arguments);
            }
            else {
                fbq.queue.push(arguments);
            }
        };
        fbq.disablePushState = true;
        fbq.push             = fbq;
        fbq.loaded           = false;
        fbq.version          = "2.0";
        fbq.queue            = [];
        window._fbq          = window._fbq || fbq;

        // Load script
        script         = document.createElement("script");
        script.async   = true;
        script.src     = "https://connect.facebook.net/en_US/fbevents.js";
        script.onload  = this.onLoad;
        script.onerror = this.onLoadError;
        document.head.appendChild(script);
    },

    /**
     * @returns  {boolean}  `true` when ready, `false` otherwise.
     * @private
     */
    verifyReadiness: function() {
        return typeof fbq !== "undefined" ? true : false;
    },

    /**
     * @private
     */
    onReady: kind.inherit(function(sup) {
        return function() {
            var id = get(config, "facebookPixel.id", null);

            // Initialize if that's our duty
            if (get(config, "facebookPixel.loader", null) === "self") {
                // Consent has been revoked while we were getting ready
                if (!this.hasLoadConsent()) {
                    this.app.$.router.reload();

                    return;
                }

                fbq("init", id);
                fbq("consent", "grant");
            }

            sup.apply(this, arguments);

            // Send PageView
            if (this.cachedPageView) {
                this.sendPageView(this.cachedPageView);
            }
        };
    }),

    // Events
    // ----------------

    /**
     * Send an event to Meta.
     *
     * @param  {Object}  params  Optional params.
     * @public
     */
    sendPageView: function(params) {
        var $router = this.app.$.router;

        // Consent check hasn't happened yet or Meta isn't loaded yet
        if (!this.isReady) {
            this.set("cachedPageView", params);

            return;
        }

        // User has consented
        fbq("trackCustom", "PageView", utils.mixin(
            {
                page_location: window.location.href,
                page_path:     $router.getCurrentPath()
            },
            params || {}
        ));
    },

    /**
     * Send an event to Meta.
     *
     * @param  {string}   category  The event category.
     * @param  {string}   action    The event action.
     * @param  {string}   label     The event label.
     * @param  {string}   value     The event value.
     * @param  {Object=}  params    Optional params.
     * @public
     */
    sendEvent: function(category, action, label, value, params) {
        if (this.isReady) {
            if (this.standardEvents.indexOf(action) !== -1) {
                fbq("track", action, params || {});
            }
            else {
                fbq("trackCustom", action, params || {});
            }
        }
    }

});
